import { Button, Paper, Tooltip } from '@mui/material';
import { GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { useCallback, useMemo } from 'react';
import { Link as NavLink } from 'react-router-dom';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import {
  DataTable,
  IColDef,
} from 'src/app/modules/Manage/pages/queues-table/DataTable';

import { agentUserId } from '../../recoil/atoms/agentUserId';
import { Role } from '../../recoil/atoms/user';
import { agentSummary } from '../../recoil/selectors/agentSummary';
import { permissionsSelector } from '../../recoil/selectors/permissionsSelector';
import { AgentSummaryBarChart } from '../components/charts/AgentSummaryBarChart';
import { MetricDropdown } from '../components/dropdowns/MetricDropdown';
import { TeamDropdown } from '../components/dropdowns/TeamDropdown';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import { EnhancedTableToolbar } from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { QueuesSkeleton } from '../modules/Skeleton/QueuesSkeleton';

// Helper function to convert percentage strings to numbers for sorting
const formatPercent = (value) => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return value;
  const str = String(value);
  return Number(str.replace(/%/g, '')) || 0;
};

export function AgentSummary(props?: { singleAgent: boolean }) {
  // require getbusiness users, callReportingData
  const setAgentId = useSetRecoilState(agentUserId);
  const agentSummaryLoadable = useRecoilValueLoadable(agentSummary);
  const agents = useMemo(
    () =>
      agentSummaryLoadable.contents?.stats?.map((o) => ({
        ...o,
        id: uuidv4(),
        // Add numeric versions for sorting
        incomingmissed_pct_numeric: formatPercent(o.incomingmissed_pct),
        outgoingconnection_pct_numeric: formatPercent(o.outgoingconnection_pct),
      })) ?? [],
    [agentSummaryLoadable.contents]
  );
  // .sort((a, b) => b.allconnections - a.allconnections);
  function viewAction(row) {
    setAgentId({
      id: row.userid,
      name: row.email,
      systemTeamId: row.systemTeamId,
      type: row.type,
    });
  }
  function actionsFormatter(params: GridRenderCellParams) {
    // create Actions element here
    return (
      <div>
        <NavLink to={`/manage/agents/${params.row.userid}/edit`}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => viewAction(params.row)}
          >
            View
          </Button>
        </NavLink>
      </div>
    );
  }
  /* Add this back when backend is updted to return Avg Duration as a Seconds number and not a string
   */
  function pad(num) {
    return ('0' + num).slice(-2);
  }

  const hhmmss = (secs) => {
    let minutes = Math.floor(secs / 60);
    secs = secs % 60;
    const hours = Math.floor(minutes / 60);
    minutes = minutes % 60;
    return `${hours}:${pad(minutes)}:${pad(secs)}`;
  };

  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role } = permissionsLoadable.contents;
  const isUser = role === Role.USER;
  const columns = [
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      renderCell: (params) =>
        params.value === 'N/A' || isUser ? (
          params.value
        ) : (
          <NavLink to={`/manage/agents/${params.row.userid}/edit`}>
            {params.value}
          </NavLink>
        ),
    },
    {
      field: 'allcalls',
      headerName: 'All Calls ⓘ',
      Header: () => (
        <Tooltip title={'All incoming and outgoing calls'}>
          <span>All Calls ⓘ</span>
        </Tooltip>
      ),
    },
    {
      field: 'totalPowerlistDials',
      headerName: 'Total Powerlist Dial Attempts ⓘ',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      width: 220,
      Header: ({ _column }) => (
        <Tooltip
          title={'Every Powerlist Dial Attempt within the specified date range'}
        >
          <span>Total Powerlist Dial Attempts ⓘ</span>
        </Tooltip>
      ),
    },
    {
      field: 'allconnections',
      headerName: 'All Connections ⓘ',
      width: 150,
      Header: () => (
        <Tooltip
          title={
            'All incoming and outgoing calls that have exceeded your Connection Duration'
          }
        >
          <span>All Connections ⓘ</span>
        </Tooltip>
      ),
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      // sortComparator: (v1, v2, row1, row2) =>
      //   row2.data.allconnections - row1.allconnections,
    },
    {
      field: 'allincoming',
      headerName: 'Incoming ⓘ',
      Header: () => (
        <Tooltip title={'All incoming calls to an agent‘s direct number'}>
          <span>Incoming ⓘ</span>
        </Tooltip>
      ),
    },
    {
      field: 'incomingconnection',
      headerName: 'Incoming Connections ⓘ',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      width: 170,
      Header: () => (
        <Tooltip
          title={
            'All incoming calls that have exceeded your Connection Duration'
          }
        >
          <span>Incoming Connections ⓘ</span>
        </Tooltip>
      ),
    },
    {
      field: 'incomingmissed',
      headerName: 'Incoming Missed ⓘ',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      width: 150,
      Header: () => (
        <Tooltip
          title={'All incoming missed calls to an agent‘s direct number'}
        >
          <span>Incoming Missed ⓘ</span>
        </Tooltip>
      ),
    },
    {
      field: 'incomingmissed_pct_numeric',
      headerName: 'Missed % ⓘ',
      type: 'number',
      enableSorting: true,
        renderCell: (params) => {
        // Display the original percentage string
        return params.row.incomingmissed_pct;
      },
      Header: () => (
        <Tooltip
          title={
            'Percentage of incoming missed calls to an agent‘s direct number'
          }
        >
          <span>Missed % ⓘ</span>
        </Tooltip>
      ),
    },
    {
      field: 'outgoingcalls',
      headerName: 'Outgoing ⓘ',
      Header: () => (
        <Tooltip title={'All outgoing calls made by an agent'}>
          <span>Outgoing ⓘ</span>
        </Tooltip>
      ),
    },
    {
      field: 'outgoingconnection',
      headerName: 'Outgoing Connections ⓘ',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      width: 170,
      Header: () => (
        <Tooltip
          title={
            'All outgoing calls that have exceeded your Connection Duration'
          }
        >
          <span>Outgoing Connections ⓘ</span>
        </Tooltip>
      ),
    },
    {
      field: 'outgoingconnection_pct_numeric',
      headerName: 'Outgoing Connection % ⓘ',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      type: 'number',
      enableSorting: true,
        renderCell: (params) => {
        // Display the original percentage string
        return params.row.outgoingconnection_pct;
      },
      Header: () => (
        <Tooltip
          title={
            'Percentage of outgoing answered calls made by an agent that exceeded your Connection Duration'
          }
        >
          <span>Outgoing Connections % ⓘ</span>
        </Tooltip>
      ),
    },
    {
      field: 'allavgduration2',
      headerName: 'Avg. Duration ⓘ',
      enableSorting: true,
      renderCell: ({ value }) => hhmmss(Math.ceil(value)),
      valueFormatter: ({ value }) => hhmmss(Math.ceil(value)),
      width: 150,
      Header: () => (
        <Tooltip title={'The average duration of all an agent‘s calls'}>
          <span>Avg. Duration ⓘ</span>
        </Tooltip>
      ),
    },
    {
      field: 'alldurationsum',
      headerName: 'Tot. Duration ⓘ',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      enableSorting: true,
      renderCell: ({ value }) => hhmmss(Math.ceil(value)),
      valueFormatter: ({ value }) => hhmmss(Math.ceil(value)),
      width: 180,
      Header: () => (
        <Tooltip
          title={
            'The total duration for all calls, including missed calls, for a given agent'
          }
        >
          <span>Tot. Duration ⓘ</span>
        </Tooltip>
      ),
    },
    {
      field: 'answeredDurationSumNum',
      headerName: 'Tot. Duration Answered ⓘ',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      enableSorting: true,
      renderCell: ({ value }) => hhmmss(Math.ceil(value)),
      valueFormatter: ({ value }) => hhmmss(Math.ceil(value)),
      width: 180,
      Header: () => (
        <Tooltip
          title={'The total duration for all answered calls for a given agent'}
        >
          <span>Tot. Duration Answered ⓘ</span>
        </Tooltip>
      ),
    },
    {
      field: 'userid',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      width: 100,
      enableSorting: false,
      renderCell: actionsFormatter,
    },
  ] /* satisfies */ as IColDef[];

  const sortModel = [
    {
      field: 'allconnections',
      sort: 'desc',
    },
  ] /* satisfies */ as Record<string, unknown>[] & GridSortModel;
  const ToolbarComponent = useCallback(
    (props) => <EnhancedTableToolbar scaleLoadingForSearch={true} {...props} />,
    []
  );
  return (
    <div style={{ maxWidth: '100%' }}>
      <Paper square={true} elevation={0}>
        {!props?.singleAgent && <TeamDropdown />}
        <MetricDropdown excludeGroups={[]} excludeMetricKeys={[]} />
        <div>
          <AgentSummaryBarChart />
        </div>
        {agentSummaryLoadable.state === 'loading' && <QueuesSkeleton />}
        <DataTable
          rows={agents}
          columns={columns}
          sortModel={sortModel}
          ToolbarComponent={ToolbarComponent}
        />
      </Paper>
    </div>
  );
}
